import type { JSX } from "react";
import { useMemo, useRef, useState } from "react";

import { Popover as BasePopover } from "@base-ui/react/popover";
import { useControlContext } from "@frontend/contexts";
import type { SupportedLocale } from "@frontend/types";

import { DatePickerContext, makeDatePickerContext } from "./DatePicker.context";
import type { DatePickerProps, DateRangeValue } from "./DatePicker.types";
import { WEEK_START } from "./constants";
import type { CalendarDate } from "./dateMath";
import {
  addMonths,
  compareDate,
  formatDate,
  parseDate,
  today,
} from "./dateMath";

function resolveWeekStartsOn(locale: Intl.LocalesArgument | undefined): 0 | 1 {
  const resolved = new Intl.DateTimeFormat(locale).resolvedOptions().locale;
  const lang = resolved.split("-")[0] as SupportedLocale;

  return WEEK_START[lang] ?? 1;
}

export function DatePicker(props: DatePickerProps): JSX.Element {
  const {
    size,
    layout = "default",
    min,
    max,
    locale,
    disabled,
    arrow,
    open,
    defaultOpen,
    onOpenChange,
    modal,
    children,
    range = false,
  } = props;

  const inherited = useControlContext();
  const resolvedSize = size ?? inherited.size;
  const pointerOffsetRef = useRef(0);
  const actionsRef = useRef<BasePopover.Root.Actions>(null);

  const isControlled = props.value !== undefined;

  const [internalValue, setInternalValue] = useState<
    string | DateRangeValue | null
  >(props.defaultValue ?? (props.range ? [null, null] : null));

  const currentValue = isControlled ? props.value : internalValue;

  const openEnd = (props.range && props.openEnd) || null;
  const requireEnd = props.range && true === props.requireEnd;

  const [hoveredDay, setHoveredDay] = useState<CalendarDate | null>(null);
  const [draftStart, setDraftStart] = useState<CalendarDate | null>(null);

  const selectedRange = useMemo<[CalendarDate | null, CalendarDate | null]>(
    () =>
      range
        ? null !== draftStart
          ? [draftStart, null]
          : [
              parseDate((currentValue as DateRangeValue | null)?.[0]),
              parseDate((currentValue as DateRangeValue | null)?.[1]),
            ]
        : [parseDate(currentValue as string | null), null],
    [range, currentValue, draftStart],
  );

  const openEnded =
    null !== openEnd &&
    null === draftStart &&
    null !== selectedRange[0] &&
    (currentValue as DateRangeValue | null)?.[1] === openEnd.value;

  const selected = range ? null : selectedRange[0];

  const initialView = selectedRange[0] ?? today();

  const [view, setView] = useState({
    year: initialView.year,
    month: initialView.month,
  });

  function commit(next: string | DateRangeValue | null) {
    if (!isControlled) {
      setInternalValue(next);
    }

    if (props.range) {
      props.onValueChange?.(next as DateRangeValue);

      return;
    }

    props.onValueChange?.(next as string | null);
  }

  function selectDay(year: number, month: number, day: number) {
    const date = { year, month, day };

    if (!range) {
      commit(formatDate(date));
      actionsRef.current?.close();

      return;
    }

    const [start, end] = selectedRange;

    if (
      null === start ||
      null !== end ||
      openEnded ||
      compareDate(date, start) < 0
    ) {
      if (requireEnd) {
        setDraftStart(date);
      } else {
        commit([formatDate(date), null]);
      }

      setHoveredDay(null);

      return;
    }

    finishRange(start, formatDate(date));
  }

  function finishRange(start: CalendarDate, end: string) {
    setDraftStart(null);
    commit([formatDate(start), end]);
    setHoveredDay(null);
    actionsRef.current?.close();
  }

  function chooseOpenEnd() {
    const [start] = selectedRange;

    if (null === openEnd || null === start) {
      return;
    }

    finishRange(start, openEnd.value);
  }

  const handleOpenChange: BasePopover.Root.Props["onOpenChange"] = (
    nextOpen,
    details,
  ) => {
    if (!nextOpen) {
      setDraftStart(null);
    }

    if (nextOpen && selectedRange[0]) {
      setView({ year: selectedRange[0].year, month: selectedRange[0].month });
    }

    onOpenChange?.(nextOpen, details);
  };

  return (
    <DatePickerContext.Provider
      value={makeDatePickerContext({
        size: resolvedSize,
        layout,
        disabled,
        arrow,
        locale,
        viewYear: view.year,
        viewMonth: view.month,
        selected,
        range,
        selectedRange,
        openEnd,
        openEnded,
        chooseOpenEnd,
        hoveredDay,
        setHoveredDay,
        min: parseDate(min),
        max: parseDate(max),
        weekStartsOn: resolveWeekStartsOn(locale),
        pointerOffsetRef,
        selectDay,
        goToMonth: (delta) => {
          setView((current) => addMonths(current.year, current.month, delta));
        },
        goToView: (year, month) => {
          setView({ year, month });
        },
      })}
    >
      <BasePopover.Root
        data-slot="date-picker-root"
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        modal={modal}
        actionsRef={actionsRef}
      >
        {children}
      </BasePopover.Root>
    </DatePickerContext.Provider>
  );
}
